import { useCallback } from 'react';

import { componentLogger } from '@ragu-components/logger';
import { Resources } from '@ragu-components/ord-resources';
import { useTranslate, HttpError } from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';

import type { AgentSettings, Queue } from '@ragu-components/ord-types';
import type { Identifier } from 'react-admin';

export interface AgentOperationResult {
  successful: string[];
  failed: string[];
  errors: Set<string>;
}

interface UseAgentOperationsReturn {
  deleteAgents: (agentPairs: Map<string, Identifier>, entityIds: string[]) => Promise<AgentOperationResult>;
  createGroupAgents: (
    groupIds: string[],
    existingPairs: Map<string, Identifier>,
    queueRecord: Queue,
    labelIds: string[],
    agentSettings: AgentSettings
  ) => Promise<AgentOperationResult>;
  createUserAgents: (
    userIds: string[],
    existingPairs: Map<string, Identifier>,
    queueRecord: Queue,
    labelIds: string[],
    agentSettings: AgentSettings
  ) => Promise<AgentOperationResult>;
}

export const useAgentOperations = (): UseAgentOperationsReturn => {
  const dataProvider = useIngordDataProvider();
  const translate = useTranslate();

  const addError = useCallback(
    (error: unknown, errors: Set<string>): void => {
      if (error instanceof HttpError) {
        errors.add(translate(error.message));
      }
    },
    [translate]
  );

  // 402 = license limit reached; 401/403 = auth failure — no point continuing the loop
  const isFatalError = useCallback((error: unknown): boolean => {
    return error instanceof HttpError && [401, 402, 403].includes(error.status as number);
  }, []);

  const deleteAgents = useCallback(
    async (agentPairs: Map<string, Identifier>, entityIds: string[]): Promise<AgentOperationResult> => {
      const successful: string[] = [];
      const failed: string[] = [];
      const errors = new Set<string>();

      const entitiesToDelete = Array.from(agentPairs.keys()).filter((id) => !entityIds.includes(id));

      let abort = false;
      for (const entityId of entitiesToDelete) {
        if (abort) {
          failed.push(entityId);
          continue;
        }
        const agentId = agentPairs.get(entityId);
        if (agentId !== undefined) {
          try {
            await dataProvider.delete(Resources.agents, { id: agentId });
            successful.push(entityId);
          } catch (error) {
            failed.push(entityId);
            componentLogger.error('Error deleting agent', error);
            addError(error, errors);
            if (isFatalError(error)) abort = true;
          }
        }
      }

      return { successful, failed, errors };
    },
    [dataProvider, addError, isFatalError]
  );

  const createGroupAgents = useCallback(
    async (
      groupIds: string[],
      existingPairs: Map<string, Identifier>,
      queueRecord: Queue,
      labelIds: string[],
      agentSettings: AgentSettings
    ): Promise<AgentOperationResult> => {
      const successful: string[] = [];
      const failed: string[] = [];
      const errors = new Set<string>();

      const newGroupIds = groupIds.filter((id) => !existingPairs.has(id));

      let abort = false;
      for (const groupId of newGroupIds) {
        if (abort) {
          failed.push(groupId);
          continue;
        }
        try {
          const groupRecord = await dataProvider.getOne(Resources.groups, { id: groupId });
          await dataProvider.create('agents', {
            data: {
              name: `Group ${queueRecord.name} ${groupRecord.data.name as string}`,
              group: groupId,
              queue: queueRecord.id,
              labels: labelIds,
              settings: agentSettings,
            },
          });
          successful.push(groupId);
        } catch (error) {
          failed.push(groupId);
          componentLogger.error('Error creating agent for group', error);
          addError(error, errors);
          if (isFatalError(error)) abort = true;
        }
      }

      return { successful, failed, errors };
    },
    [dataProvider, addError, isFatalError]
  );

  const createUserAgents = useCallback(
    async (
      userIds: string[],
      existingPairs: Map<string, Identifier>,
      queueRecord: Queue,
      labelIds: string[],
      agentSettings: AgentSettings
    ): Promise<AgentOperationResult> => {
      const successful: string[] = [];
      const failed: string[] = [];
      const errors = new Set<string>();

      const newUserIds = userIds.filter((id) => !existingPairs.has(id));

      let abort = false;
      for (const userId of newUserIds) {
        if (abort) {
          failed.push(userId);
          continue;
        }
        try {
          const userRecord = await dataProvider.getOne(Resources.users, { id: userId });
          await dataProvider.create('agents', {
            data: {
              name: `User ${queueRecord.name} ${userRecord.data.firstname as string} ${
                userRecord.data.lastname as string
              }`,
              user: userId,
              queue: queueRecord.id,
              labels: labelIds,
              settings: agentSettings,
            },
          });
          successful.push(userId);
        } catch (error) {
          failed.push(userId);
          componentLogger.error('Error creating agent for user', error);
          addError(error, errors);
          if (isFatalError(error)) abort = true;
        }
      }

      return { successful, failed, errors };
    },
    [dataProvider, addError, isFatalError]
  );

  return {
    deleteAgents,
    createGroupAgents,
    createUserAgents,
  };
};
